import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "../utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { showDialog } from "@widgets/CommonDialog";
import { ClearIcon } from "@mui/x-date-pickers";
import { global } from "@shared/libs/Global";
import { onSearchStore } from "@shared/libs/Global";

interface SnItemInfoBoxProps {
  defaultValue?: any;
  item_name?: any;
  dc_item_spec?: any;
  dc_item_unit?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (code: string, name: string, rowData?: any) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  searchRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnItemInfoBox = React.memo(
  ({
    code,
    cd_clas,
    defaultValue,
    item_name,
    dc_item_spec,
    dc_item_unit,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    searchRef,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnItemInfoBoxProps) => {
    const { userInfo } = global();
    const [value, setValue] = useState(defaultValue);
    const [displayValue, setDisplayValue] = useState(defaultValue?.code || "");
    const [nameValue, setNameValue] = useState(item_name);
    const [specValue, setSpecValue] = useState(dc_item_spec);
    const [unitValue, setUnitValue] = useState(dc_item_unit);
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [backgroundColor, setBackgroundColor] = useState(
      isEditable ? "white" : "#F1F1F1"
    );

    const { runOnSearch } = onSearchStore();

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);
    // Enter/Tab 처리 중 발생하는 blur 중복 실행 방지용
    const skipBlurRef = useRef(false);
    // 이 컴포넌트가 직접 조회해 확정한 품목 행 (부모 formData가 따라올 때까지의 근거)
    const confirmedRowRef = useRef<any>(null);
    // const [clearValue, setClearValue] = useState(false);

    // 팝업 세팅용
    const [title, setTitle] = useState("");
    const [width, setWidth] = useState<any>();
    const [height, setHeight] = useState<any>();
    const [autoSearch, setAutoSearch] = useState(true);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    useEffect(() => {
      const singlePopupData = getSearchData(code, undefined);
      setTitle(singlePopupData.title);
      setAutoSearch(singlePopupData.autoSearch);
      setWidth(singlePopupData.width);
      setHeight(singlePopupData.height);
    }, [code]);

    // 부모가 defaultValue를 인라인 객체로 넘기므로 객체 자체를 의존성으로 두면
    // 부모가 렌더될 때마다 동기화가 돌아 화면 값이 깜빡인다. 실제 값으로 비교한다.
    const codeProp = defaultValue?.code ?? "";
    const nameProp = defaultValue?.name ?? "";
    const prevCodePropRef = useRef<string | undefined>(undefined);

    useEffect(
      function () {
        const codeChanged = prevCodePropRef.current !== codeProp;
        prevCodePropRef.current = codeProp;

        setValue({ code: codeProp, name: nameProp });
        setDisplayValue(codeProp);

        // nm_item은 onCodeValueChanged가 동기로 formData에 넣어주지만,
        // 규격/단위는 화면의 onHeaderValueChanged(비동기)가 돌려줘야만 들어온다.
        // 그 사이(또는 화면이 안 돌려주는 경우) 부모 값으로 덮어써서 값이 사라지므로,
        // 이 컴포넌트가 방금 확정한 품목이면 자체 조회 결과를 유지한다.
        const confirmed =
          codeProp && confirmedRowRef.current?.code === codeProp
            ? confirmedRowRef.current
            : null;

        setNameValue(item_name || confirmed?.name || nameProp || "");

        if (confirmed) {
          setSpecValue(dc_item_spec || confirmed.dc_item_spec || "");
          setUnitValue(dc_item_unit || confirmed.dc_item_unit || "");
        } else {
          if (dc_item_spec || codeChanged) setSpecValue(dc_item_spec ?? "");
          if (dc_item_unit || codeChanged) setUnitValue(dc_item_unit ?? "");
        }
      },
      [codeProp, nameProp, item_name, dc_item_spec, dc_item_unit]
    );

    useEffect(() => setBorderWidth(isRequired ? "2px" : "1px"), [isRequired]);

    useEffect(
      () => setBackgroundColor(isEditable ? "white" : "#F1F1F1"),
      [isEditable]
    );

    const openPopup = async (inputValue?: string) => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const formData = searchRef?.current?.getFormdata() || {};
        const mergedConditions = popupConditions
          ? { ...formData, ...popupConditions }
          : formData;

        const mergedData = {
          ...mergedConditions,
          str_search: inputValue || displayValue,
          cd_clas: cd_clas || "",
        };

        let data: any;

        const result = await showPopupModal({
          title: title,
          children: (
            <SinglePopup
              code={code}
              selectedData={mergedData}
              autoSearch={autoSearch}
            />
          ),
          width: width,
          height: height,
        });
        data = handleReturnFunction(result);
        if (data === "close") {
          // setValue({ code: displayValue, name: "" });
          // setDisplayValue(displayValue);
          // onValueChanged?.(displayValue, "");
          // onFocus && onFocus("current");
          return;
        } else if (data.length > 0) {
          confirmedRowRef.current = data[0];
          setValue(data[0]);
          setNameValue(data[0].name);
          setSpecValue(data[0].dc_item_spec);
          setUnitValue(data[0].dc_item_unit);
          if (isSearchPanel) {
            setDisplayValue(data[0].code);
            onValueChanged?.(data[0].code, data[0].name);
            onFocus && onFocus("current");
          } else {
            setDisplayValue(data[0].code);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            onFocus && onFocus("next");
          }
          return data[0];
        } else {
          // 팝업에서 취소 눌렀을 때
          clearValue();
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setFocused(true);
      setDisplayValue(value.code);
    };

    // 확정된 품목 행을 반환한다. (없으면 undefined)
    const handleBlur = async (key?: string) => {
      // Enter/Tab 처리 중 포커스 이동으로 발생한 blur는 중복 조회를 막기 위해 무시
      if (!key && skipBlurRef.current) return;

      setFocused(false);

      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (code === "POPUP_POSTCODE") {
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          return await openPopup();
        }
        if (key === "Tab") onFocus && onFocus("next");
        return;
      }

      if (!displayValue) {
        // 입력값이 없을 때
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          return await openPopup();
        }
        // setClearValue(true);
        clearValue();
        if (key === "Tab") onFocus && onFocus("next");
        return;
      }

      // 입력값이 있을 때
      if (key === "searchBtn" && !isOpenRef.current) {
        return await openPopup(value.code || displayValue);
      }

      const data = await checkData();
      if (!data || data.length !== 1) {
        if (!isOpenRef.current) return await openPopup(displayValue);
        return;
      }

      const row = data[0];
      confirmedRowRef.current = row;
      setValue(row);
      setDisplayValue(row.code);
      setNameValue(row.name);
      setSpecValue(row.dc_item_spec);
      setUnitValue(row.dc_item_unit);

      // 부모의 onCodeValueChanged는 화면의 onHeaderValueChanged(비동기)를 거쳐
      // 규격/단위를 formData에 채운다. 이걸 기다리지 않고 재조회가 먼저 돌면
      // formData가 아직 비어 있는 상태로 덮여 값이 사라졌다 나타난다.
      const valueChanged = onValueChanged?.(
        row.code,
        row.name,
        row
      ) as Promise<unknown> | void;

      if (key === "Enter" && isSearchPanel) {
        setFocused(true);
        onFocus && onFocus("current");
      } else if (key === "Enter" || key === "Tab") {
        onFocus && onFocus("next");
      }

      await valueChanged;

      return row;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(e.target.value);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" && e.key !== "Tab") return;

      e.preventDefault();
      // 포커스 이동으로 뒤따르는 blur가 checkData를 재실행하지 않도록 차단
      skipBlurRef.current = true;
      try {
        const selected = await handleBlur(e.key);
        if (e.key === "Enter" && selected?.code) {
          await runOnSearch();
        }
      } finally {
        setTimeout(() => {
          skipBlurRef.current = false;
        }, 0);
      }
    };

    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        // if (!popupSetting || value?.code === displayValue) return;
        if (!popupSetting) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            [popupSetting.str_search]: displayValue,
            cd_clas: cd_clas || "",
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: {
            cd_firm: userInfo.cd_firm,
            str_search: displayValue,
            cd_clas: cd_clas || "",
          },
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        return data.map((d: any) => ({
          ...d,
          code: d[popupSetting.columns[0]],
          name: d[popupSetting.columns[1]],
        }));
      }
    };

    const handleClear = async (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // setClearValue(true);
      setTimeout(() => {
        clearValue();
      }, 0);
      onFocus && onFocus("current");
    };

    const clearValue = () => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      confirmedRowRef.current = null;
      onValueChanged?.("", "", {});
      setValue({ code: "", name: "" });
      setDisplayValue("");
      setNameValue("");
      setSpecValue("");
      setUnitValue("");
      // if (index && inputEls) inputEls.current[index].value = "";
    };

    return (
      <div className="relative w-full" style={{ padding: 0 }}>
        <TextField
          inputRef={inputRef}
          value={displayValue}
          onFocus={handleFocus}
          onBlur={() => handleBlur()}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseOver={() => setMouseOver(true)}
          onMouseOut={() => setMouseOver(false)}
          fullWidth
          disabled={isReadOnly}
          slotProps={{
            input: {
              autoComplete: "off",
              readOnly: isReadOnly,
              endAdornment: (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {(isFocused || isMouseOver) &&
                    !isReadOnly &&
                    displayValue && (
                      <IconButton
                        edge="end"
                        onClick={handleClear}
                        onMouseDown={(event) => event.preventDefault()}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      handleBlur("searchBtn");
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            },
          }}
          sx={{
            mr: 1,
            backgroundColor,
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
        <TextField
          value={nameValue}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          fullWidth
          sx={{
            mr: 1,
            backgroundColor: "#dadcdc",
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
        <TextField
          value={specValue}
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={{
            mr: 1,
            backgroundColor: "#dadcdc",
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
        <TextField
          value={unitValue}
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={{
            backgroundColor: "#dadcdc",
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
      </div>
    );
  }
);
export default SnItemInfoBox;
